import { FormikField, Text, Input, VStack } from "flicket-ui";
import { useFormikContext } from "formik";
import { useSinglePageCheckoutContext } from "../hooks/singlePageCheckoutContext";
import { MobileDivider } from "./MobileDivider";
import { Select } from "~components";
import { HeardAboutUsOption, HEARD_ABOUT_US_OPTIONS } from "../utils/config";
import { HeardAboutUsType, Order } from "~graphql/sdk";
import { AnimateReveal } from "~components/AnimateReveal/AnimateReveal";
import * as Yup from "yup";
import { useContext } from "react";
import { BaseContext } from "~context";
import { PickTypes } from "~lib/helpers/types";

export interface FormValues {
  heardAboutUs: HeardAboutUsType | undefined;
  heardAboutUsDetails: string | undefined;
  dietaryRequirements: string | undefined;
}

export const initialValues = (): FormValues => ({
  heardAboutUs: undefined,
  heardAboutUsDetails: undefined,
  dietaryRequirements: undefined,
});

export const schema: Yup.ObjectSchema<FormValues> = Yup.object({
  heardAboutUs: Yup.mixed()
    .oneOf(Object.values(HeardAboutUsType))
    .when("$askHowdidYouhearAboutUs", {
      is: true,
      then: (schema: Yup.MixedSchema) =>
        schema.required("Please select an option"),
      otherwise: (schema: Yup.MixedSchema) => schema.optional(),
    }),
  heardAboutUsDetails: Yup.string().when("heardAboutUs", {
    is: HeardAboutUsType.Other,
    then: Yup.string().required("Please specify how you heard about us"),
    otherwise: Yup.string().optional(),
  }),
  dietaryRequirements: Yup.string().optional(),
});

export const PrePurchaseQuestionsFormPartial = () => {
  const { order } = useSinglePageCheckoutContext();
  const { organization } = useContext(BaseContext);

  const { getFieldHelpers, getFieldProps, isSubmitting } =
    useFormikContext<FormValues>();

  const heardAboutUsProps = getFieldProps("heardAboutUs");
  const heardAboutUsHelper = getFieldHelpers("heardAboutUs");
  const heardAboutUsDetailsHelper = getFieldHelpers("heardAboutUsDetails");

  const { askForDietaryRequirements, askHowdidYouhearAboutUs } =
    getActiveFields(order, organization.id);

  const hasAdditionalFields =
    askForDietaryRequirements || askHowdidYouhearAboutUs;

  if (!hasAdditionalFields) {
    return null;
  }

  return (
    <>
      {askHowdidYouhearAboutUs && (
        <>
          <VStack gap={0}>
            <FormikField
              label={<Text variant="header.S">How did you hear about us?</Text>}
              name="heardAboutUs"
              as={Select}
              fieldProps={{
                options: HEARD_ABOUT_US_OPTIONS,
                value:
                  HEARD_ABOUT_US_OPTIONS.find(
                    (option) => option.value === heardAboutUsProps.value
                  ) || null,
                onChange: ({ value }: HeardAboutUsOption) => {
                  heardAboutUsHelper.setError(undefined);
                  heardAboutUsHelper.setValue(value);
                },
              }}
              disabled={isSubmitting}
            />
            <AnimateReveal
              show={heardAboutUsProps?.value === HeardAboutUsType.Other}
              initial={false}
            >
              <FormikField
                mt={2}
                name="heardAboutUsDetails"
                as={Input}
                fieldProps={{
                  inputAs: "textarea",
                  // We need to override the incorrect valid prop from useFormikField
                  isValid: true,
                  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => {
                    heardAboutUsDetailsHelper.setError(undefined);
                    heardAboutUsDetailsHelper.setValue(e.target.value);
                  },
                }}
                placeholder="Please specify (Optional)"
                disabled={isSubmitting}
              />
            </AnimateReveal>
          </VStack>
          <MobileDivider />
        </>
      )}

      {askForDietaryRequirements && (
        <>
          <FormikField
            label={<Text variant="header.S">Dietary requirements</Text>}
            name="dietaryRequirements"
            as={Input}
            fieldProps={{
              inputAs: "textarea",
              // We need to override the incorrect valid prop from useFormikField
              isValid: true,
            }}
            disabled={isSubmitting}
          />
          <MobileDivider />
        </>
      )}
    </>
  );
};

export function getActiveFields(
  order: PickTypes<
    Order,
    {
      release:
        | {
            askHowDidYouHearAboutUs: true;
            askForDietaryRequirements: true;
          }
        | undefined;
      membership: {
        id: true;
      };
    }
  >,
  orgId?: string
) {
  const askHowdidYouhearAboutUs =
    order?.release?.askHowDidYouHearAboutUs ||
    // Always show for Wellington Phoenix memberships
    (orgId === "b27b5a7e-1a19-4f38-be65-7d7443ebd1a8" && !!order?.membership);

  const askForDietaryRequirements = order?.release?.askForDietaryRequirements;

  return {
    askForDietaryRequirements,
    askHowdidYouhearAboutUs,
  };
}
